import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  MapPin,
  ListTree,
  Package,
  Users,
  Star,
  ArrowRight,
  Clock,
} from "lucide-react";
import { normalizeSector } from "../utils/normalizeSector";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";

const timeAgo = (date) => {
  if (!date) return "";
  const ms = Date.now() - date.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "az önce";
  if (m < 60) return `${m} dk`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} sa`;
  const d = Math.floor(h / 24);
  return `${d} gün`;
};

const FirmaCard = ({ firm, onClick }) => {
  const initials = (firm.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toLocaleUpperCase("tr") || "")
    .join("");

  return (
    <div
      onClick={() => onClick?.(firm)}
      className="group relative bg-white rounded-2xl border border-slate-600/30 p-4 flex gap-3.5
  hover:border-amber-400 hover:shadow-xl hover:shadow-amber-100/50 hover:-translate-y-0.5
  transition-all duration-200 cursor-pointer overflow-hidden shadow-sm"
    >
      {/* Gradient accent üst çizgi */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 via-amber-300 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Logo */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center shadow-sm">
        {firm.logoUrl ? (
          <img
            src={firm.logoUrl}
            alt={firm.name}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <span className="text-sm font-bold text-slate-500">{initials}</span>
        )}
      </div>

      {/* Bilgiler */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-slate-800 text-sm leading-snug line-clamp-1 group-hover:text-amber-700 transition-colors">
            {firm.name}
          </h4>
          <div className="flex items-center gap-1.5 shrink-0">
            {firm.isGold && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                ⭐ Gold
              </span>
            )}
            {firm.isNew && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-semibold">
                YENİ
              </span>
            )}
          </div>
        </div>

        <p className="text-xs text-amber-700 font-medium mt-0.5 truncate">
          {firm.category || "—"}
        </p>

        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3 text-slate-300 shrink-0" />
            <span className="text-xs text-slate-400 truncate">
              {[firm.il, firm.ilce].filter(Boolean).join(", ") || "—"}
            </span>
          </div>
          {firm.updateDate && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-300">
              <Clock className="w-2.5 h-2.5" />
              {firm.updateDate}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentlyUpdatedFirms = ({ setCurrentPage }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState("new");
  const [fbFirms, setFbFirms] = React.useState([]);

  React.useEffect(() => {
    const q = query(
      collection(db, "firmalar"),
      orderBy("createdAt", "desc"),
      limit(24)
    );
    const unsub = onSnapshot(q, (snap) => {
      setFbFirms(
        snap.docs.map((d) => {
          const x = d.data() || {};
          const createdAt = x.createdAt?.toDate ? x.createdAt.toDate() : null;
          return {
            id: d.id,
            fbId: d.id,
            name: (x.firmaAdi || "").trim() || "İsimsiz Firma",
            logoUrl: x.logoUrl || "",
            category: normalizeSector(
              Array.isArray(x.secilenSektorler) && x.secilenSektorler.length
                ? x.secilenSektorler[0]
                : x.sektor || ""
            ),
            il: x.sehir || "",
            ilce: x.ilce || "",
            updateDate: createdAt ? timeAgo(createdAt) : "",
            isNew: true,
          };
        })
      );
    });
    return () => unsub();
  }, []);
  const handleFirmClick = (firm) => {
    navigate(`/firms/${firm.fbId}`, { state: { firm } });
  };

  const visible =
    activeTab === "new" ? fbFirms.slice(0, 12) : fbFirms.slice(0, 24);

  return (
    <div className="w-full">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900 tracking-tight">
            Son Eklenen Firmalar
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {fbFirms.length} firma kayıtlı
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { v: "new", label: "Son 12" },
            { v: "all", label: "Tümü" },
          ].map(({ v, label }) => (
            <button
              key={v}
              onClick={() => setActiveTab(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                activeTab === v
                  ? "bg-slate-900 text-white border-slate-900"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {visible.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {visible.map((firm) => (
            <FirmaCard key={firm.id} firm={firm} onClick={handleFirmClick} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-slate-300">
          <Building2 className="w-10 h-10 mb-3" />
          <p className="text-sm">Henüz firma eklenmemiş.</p>
        </div>
      )}

      <div className="mt-6 flex justify-center">
        <button
          onClick={() => setCurrentPage("firms")}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold transition"
        >
          Tüm Firmaları Gör
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const BottomStatistics = () => {
  const stats = [
    { icon: Building2, value: "2,547", label: "Toplam Firma" },
    { icon: MapPin, value: "81", label: "Şehir" },
    { icon: ListTree, value: "156", label: "Kategori" },
    { icon: Package, value: "10,234", label: "Ürün" },
    { icon: Users, value: "45,678", label: "Kullanıcı" },
    { icon: Star, value: "324", label: "Gold Üye" },
  ];

  return (
    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {stats.map(({ icon: Icon, value, label }, i) => (
        <div
          key={i}
          className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col items-center text-center hover:border-slate-200 hover:shadow-sm transition"
        >
          <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-2.5">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
          <span className="text-xl font-bold text-slate-900 tabular-nums">
            {value}
          </span>
          <span className="text-[11px] text-slate-400 mt-0.5 font-medium uppercase tracking-wide">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
};

const RecentFirmsAndEventsSection = ({ setCurrentPage }) => {
  return (
    <div className="bg-slate-50 py-10">
      <div className="container mx-auto px-4 max-w-screen-xl">
        <RecentlyUpdatedFirms setCurrentPage={setCurrentPage} />
        <BottomStatistics />
      </div>
    </div>
  );
};

export default RecentFirmsAndEventsSection;
